/**
 * Mappers for reconciling UI types with Database types
 *
 * These functions transform between:
 * - IssueRow (database schema - snake_case, JSONB metadata, enum strings)
 * - Issue    (UI schema - camelCase, display-friendly labels)
 */

import type {
  Issue,
  IssueType,
  IssueSeverity,
  IssueStatus,
  IssueSource,
  IssueBlockerCategory,
  IssueAffectedArea,
} from "@/data/types";
import type { IssueRow } from "@/services/issue.service";

// ── Status mapping between frontend labels and DB enum ────────────────────

export const ISSUE_STATUS_TO_DB: Record<string, string> = {
  "Open": "open",
  "In Progress": "in_progress",
  "Resolved": "resolved",
  "Closed": "closed",
};

export const ISSUE_STATUS_FROM_DB: Record<string, IssueStatus> = {
  "open": "Open",
  "triaged": "Open",
  "in_progress": "In Progress",
  "in progress": "In Progress",  // legacy — kept for any stale rows
  "resolved": "Resolved",
  "closed": "Closed",
  "cancelled": "Closed",
};

// ── Severity mapping between frontend labels and DB enum ──────────────────

export const ISSUE_SEVERITY_TO_DB: Record<string, string> = {
  Low: "low",
  Medium: "medium",
  High: "high",
  Urgent: "critical",
};

export const ISSUE_SEVERITY_FROM_DB: Record<string, IssueSeverity> = {
  low: "Low",
  medium: "Medium",
  high: "Urgent",
  critical: "Urgent",
};

// ── DB → UI ───────────────────────────────────────────────────────────────

export function toUiIssue(db: IssueRow): Issue {
  const m = db.metadata ?? {};

  return {
    id: db.issue_number ?? db.id,
    jobId: db.job_id ?? "",
    issueType: (m.issueType ?? (db.title as IssueType)) ?? "Other",
    severity: (db.severity ? ISSUE_SEVERITY_FROM_DB[db.severity] : undefined) ?? "Low",
    description: db.description ?? "",
    photo: m.photo ?? "",
    owner: m.owner ?? "",
    status: (db.status ? ISSUE_STATUS_FROM_DB[db.status] : undefined) ?? "Open",
    dateCreated: db.created_at.split("T")[0],
    source: (m.source as IssueSource) ?? "Admin",
    relatedContractorId: m.relatedContractorId,
    resolutionNotes: db.resolution_notes ?? undefined,
    dateInProgress: m.dateInProgress,
    dateResolved: db.resolved_at ? db.resolved_at.split("T")[0] : undefined,
    dateClosed: m.dateClosed,
    blockerCategory: m.blockerCategory as IssueBlockerCategory | undefined,
    blockerDetail: m.blockerDetail,
    homeownerConcern: m.homeownerConcern,
    affectedArea: m.affectedArea as IssueAffectedArea | undefined,
    suggestedAction: m.suggestedAction,
    jobStageAtCreation: m.jobStageAtCreation,
    customerName: m.customerName,
    jobNumber: m.jobNumber,
  };
}

// ── UI → DB (create) ──────────────────────────────────────────────────────

export function toDbIssueCreate(ui: Issue): Omit<IssueRow, "id" | "created_at" | "updated_at"> {
  return {
    organization_id: null,
    job_id: ui.jobId || null,
    issue_number: ui.id,
    title: ui.issueType || null,
    description: ui.description || null,
    status: ui.status ? ISSUE_STATUS_TO_DB[ui.status] ?? "open" : "open",
    severity: ui.severity ? ISSUE_SEVERITY_TO_DB[ui.severity] ?? ui.severity.toLowerCase() : null,
    resolved_at: ui.dateResolved ? new Date().toISOString() : null,
    reported_by: null,
    assigned_to: null,
    resolution_notes: ui.resolutionNotes || null,
    metadata: {
      issueType: ui.issueType,
      photo: ui.photo || undefined,
      owner: ui.owner || undefined,
      source: ui.source,
      relatedContractorId: ui.relatedContractorId,
      dateInProgress: ui.dateInProgress,
      dateClosed: ui.dateClosed,
      blockerCategory: ui.blockerCategory,
      blockerDetail: ui.blockerDetail,
      homeownerConcern: ui.homeownerConcern,
      affectedArea: ui.affectedArea,
      suggestedAction: ui.suggestedAction,
      jobStageAtCreation: ui.jobStageAtCreation,
      customerName: ui.customerName,
      jobNumber: ui.jobNumber,
    },
  };
}

// ── UI → DB (update) ──────────────────────────────────────────────────────

export function toDbIssueUpdate(ui: Partial<Issue>): Partial<Omit<IssueRow, "id" | "created_at" | "updated_at">> {
  const patch: Partial<Omit<IssueRow, "id" | "created_at" | "updated_at">> = {};

  // job_id is a UUID FK — excluded here because jobId on the UI Issue may have been
  // overwritten with a human-readable job_number by enrichWithCustomerNames.
  // Job reassignment is not a supported update operation via this path.
  if (ui.issueType !== undefined) patch.title = ui.issueType || null;
  if (ui.description !== undefined) patch.description = ui.description || null;
  if (ui.status !== undefined) patch.status = ISSUE_STATUS_TO_DB[ui.status] ?? "open";
  if (ui.severity !== undefined)
    patch.severity = ISSUE_SEVERITY_TO_DB[ui.severity] ?? ui.severity.toLowerCase();
  if (ui.dateResolved !== undefined)
    patch.resolved_at = ui.dateResolved ? new Date().toISOString() : null;
  if (ui.resolutionNotes !== undefined) patch.resolution_notes = ui.resolutionNotes || null;

  const metadataFields: (keyof Issue)[] = [
    "issueType", "photo", "owner", "source", "relatedContractorId",
    "dateInProgress", "dateClosed", "blockerCategory", "blockerDetail",
    "homeownerConcern", "affectedArea", "suggestedAction",
    "jobStageAtCreation", "customerName", "jobNumber",
  ];

  // Only include metadata fields that were explicitly provided — avoids wiping
  // fields not present in the partial update.
  if (metadataFields.some((k) => k in ui)) {
    const metaPatch: Record<string, unknown> = {};
    if ("issueType" in ui) metaPatch.issueType = ui.issueType;
    if ("photo" in ui) metaPatch.photo = ui.photo || undefined;
    if ("owner" in ui) metaPatch.owner = ui.owner || undefined;
    if ("source" in ui) metaPatch.source = ui.source;
    if ("relatedContractorId" in ui) metaPatch.relatedContractorId = ui.relatedContractorId;
    if ("dateInProgress" in ui) metaPatch.dateInProgress = ui.dateInProgress;
    if ("dateClosed" in ui) metaPatch.dateClosed = ui.dateClosed;
    if ("blockerCategory" in ui) metaPatch.blockerCategory = ui.blockerCategory;
    if ("blockerDetail" in ui) metaPatch.blockerDetail = ui.blockerDetail;
    if ("homeownerConcern" in ui) metaPatch.homeownerConcern = ui.homeownerConcern;
    if ("affectedArea" in ui) metaPatch.affectedArea = ui.affectedArea;
    if ("suggestedAction" in ui) metaPatch.suggestedAction = ui.suggestedAction;
    if ("jobStageAtCreation" in ui) metaPatch.jobStageAtCreation = ui.jobStageAtCreation;
    if ("customerName" in ui) metaPatch.customerName = ui.customerName;
    if ("jobNumber" in ui) metaPatch.jobNumber = ui.jobNumber;
    patch.metadata = metaPatch as IssueRow["metadata"];
  }

  return patch;
}

// ── Batch ─────────────────────────────────────────────────────────────────

export function toUiIssues(rows: IssueRow[]): Issue[] {
  return rows.map(toUiIssue);
}
